import api from '../lib/api';

// Types
export interface DayInReview {
  id: string;
  date: string;
  organizationId: string;
  leaderOfDayId?: string;
  leaderRating?: number;
  leaderNotes?: string;
  overallScore?: number;
  narrativeSummary?: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';
  reviewedById?: string;
  reviewedAt?: string;
  scheduleChanges: ScheduleChange[];
  forecastMetrics: ForecastMetric[];
  staffingEvents: StaffingEvent[];
  channelMetrics: ChannelMetric[];
  leaderDecisions: LeaderDecision[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    scheduleChanges: number;
    staffingEvents: number;
    leaderDecisions: number;
  };
}

export interface ScheduleChange {
  id: string;
  dayInReviewId: string;
  changeType: ScheduleChangeType;
  timestamp: string;
  description: string;
  changedById?: string;
  agentId?: string;
  shiftId?: string;
  previousValue?: Record<string, any>;
  newValue?: Record<string, any>;
  impactScore?: number;
  impactNotes?: string;
  createdAt: string;
}

export type ScheduleChangeType =
  | 'SHIFT_ADDED'
  | 'SHIFT_REMOVED'
  | 'SHIFT_MODIFIED'
  | 'BREAK_ADJUSTED'
  | 'OVERTIME_ADDED'
  | 'AGENT_REASSIGNED'
  | 'CHANNEL_PRIORITY_CHANGED'
  | 'QUEUE_ROUTING_CHANGED';

export interface ForecastMetric {
  id: string;
  dayInReviewId: string;
  intervalStart: string;
  intervalEnd: string;
  channel?: string;
  forecastVolume: number;
  forecastAHT: number;
  forecastServiceLevel: number;
  forecastOccupancy: number;
  forecastShrinkage: number;
  forecastStaffing: number;
  actualVolume?: number;
  actualAHT?: number;
  actualServiceLevel?: number;
  actualOccupancy?: number;
  actualShrinkage?: number;
  actualStaffing?: number;
  volumeVariance?: number;
  ahtVariance?: number;
  serviceLevelVariance?: number;
  occupancyVariance?: number;
  shrinkageVariance?: number;
  staffingVariance?: number;
  createdAt: string;
}

export interface StaffingEvent {
  id: string;
  dayInReviewId: string;
  eventType: StaffingEventType;
  timestamp: string;
  agentId?: string;
  plannedValue?: string;
  actualValue?: string;
  duration?: number;
  staffingImpact?: number;
  notes?: string;
  createdAt: string;
}

export type StaffingEventType =
  | 'CALLOUT'
  | 'NO_SHOW'
  | 'LATE_ARRIVAL'
  | 'EARLY_DEPARTURE'
  | 'PTO_APPROVED'
  | 'PTO_DENIED'
  | 'OVERTIME_REQUESTED'
  | 'OVERTIME_APPROVED'
  | 'OVERTIME_WORKED'
  | 'BREAK_EXTENDED'
  | 'BREAK_SHORTENED'
  | 'BREAK_MOVED';

export interface ChannelMetric {
  id: string;
  dayInReviewId: string;
  channel: string;
  priorityScore: number;
  priorityChanges: number;
  targetServiceLevel: number;
  achievedServiceLevel: number;
  wasDeprioritized: boolean;
  deprioritizedFor?: string;
  tradeoffJustification?: string;
  createdAt: string;
}

export interface LeaderDecision {
  id: string;
  dayInReviewId: string;
  timestamp: string;
  decisionType: LeaderDecisionType;
  description: string;
  situationBefore?: string;
  actionTaken?: string;
  outcome?: string;
  wasEffective?: boolean;
  evaluationNotes?: string;
  createdAt: string;
}

export type LeaderDecisionType =
  | 'STAFFING_ADJUSTMENT'
  | 'BREAK_COORDINATION'
  | 'OVERTIME_APPROVAL'
  | 'CHANNEL_PRIORITIZATION'
  | 'ESCALATION_HANDLING'
  | 'PROACTIVE_PLANNING'
  | 'COMMUNICATION'
  | 'OTHER';

// DTOs
export interface CreateDayInReviewDto {
  date: string;
  leaderOfDayId?: string;
}

export interface UpdateDayInReviewDto {
  leaderOfDayId?: string;
  leaderRating?: number;
  leaderNotes?: string;
  status?: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';
  narrativeSummary?: string;
}

export interface CreateDecisionDto {
  decisionType: LeaderDecisionType;
  timestamp: string;
  description: string;
  situationBefore?: string;
  actionTaken?: string;
  outcome?: string;
}

export interface EvaluateDecisionDto {
  wasEffective: boolean;
  evaluationNotes?: string;
}

// Summaries
export interface ChangesSummary {
  totalChanges: number;
  byType: Record<string, number>;
  positiveImpact: number;
  negativeImpact: number;
  neutralImpact: number;
}

export interface ForecastSummary {
  totalIntervals: number;
  volume: {
    forecast: number;
    actual: number;
    variance: number;
    intervalsWithData: number;
  };
  serviceLevel: {
    averageVariance: number;
    intervalsWithData: number;
  };
  occupancy: {
    averageVariance: number;
    intervalsWithData: number;
  };
  staffing: {
    totalVariance: number;
    averageVariance: number;
    intervalsWithData: number;
  };
}

export interface StaffingSummary {
  totalEvents: number;
  byType: Record<string, number>;
  totalStaffingImpact: number;
  callouts: number;
  lateArrivals: number;
  earlyDepartures: number;
  overtimeApproved: number;
  breakAdjustments: number;
  ptoCount: number;
}

export interface Anomaly {
  intervalStart: string;
  type: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

export interface ShrinkageComparison {
  expectedShrinkagePercent: number;
  actualShrinkagePercent: number;
  expectedShrinkageImpact: number;
  actualShrinkageImpact: number;
  variance: number;
  wasHigherThanExpected: boolean;
}

export interface LeaderHistory {
  id: string;
  date: string;
  leaderRating: number;
  leaderNotes?: string;
  overallScore?: number;
  status: string;
}

// Service
class DayInReviewService {
  async getAll(): Promise<DayInReview[]> {
    const response = await api.get('/day-in-review');
    return response.data;
  }

  async getById(id: string): Promise<DayInReview> {
    const response = await api.get(`/day-in-review/${id}`);
    return response.data;
  }

  async getByDate(date: string): Promise<DayInReview | null> {
    const response = await api.get(`/day-in-review/by-date/${date}`);
    return response.data;
  }

  async generate(dto: CreateDayInReviewDto): Promise<DayInReview> {
    const response = await api.post('/day-in-review/generate', dto);
    return response.data;
  }

  async regenerate(id: string): Promise<DayInReview> {
    const response = await api.post(`/day-in-review/${id}/regenerate`);
    return response.data;
  }

  async update(id: string, dto: UpdateDayInReviewDto): Promise<DayInReview> {
    const response = await api.patch(`/day-in-review/${id}`, dto);
    return response.data;
  }

  async complete(id: string): Promise<DayInReview> {
    const response = await api.post(`/day-in-review/${id}/complete`);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/day-in-review/${id}`);
  }

  // Leader management
  async assignLeader(id: string, leaderOfDayId: string): Promise<DayInReview> {
    const response = await api.post(`/day-in-review/${id}/leader`, { leaderOfDayId });
    return response.data;
  }

  async evaluateLeader(id: string, rating: number, notes?: string): Promise<DayInReview> {
    const response = await api.patch(`/day-in-review/${id}/leader/evaluate`, { rating, notes });
    return response.data;
  }

  async getLeaderHistory(userId: string): Promise<LeaderHistory[]> {
    const response = await api.get(`/day-in-review/leaders/${userId}/history`);
    return response.data;
  }

  // Decision management
  async addDecision(reviewId: string, dto: CreateDecisionDto): Promise<LeaderDecision> {
    const response = await api.post(`/day-in-review/${reviewId}/decisions`, dto);
    return response.data;
  }

  async evaluateDecision(
    reviewId: string,
    decisionId: string,
    dto: EvaluateDecisionDto
  ): Promise<LeaderDecision> {
    const response = await api.patch(`/day-in-review/${reviewId}/decisions/${decisionId}`, dto);
    return response.data;
  }

  // Summaries
  async getChangesSummary(id: string): Promise<ChangesSummary> {
    const response = await api.get(`/day-in-review/${id}/summary/changes`);
    return response.data;
  }

  async getForecastSummary(id: string): Promise<ForecastSummary> {
    const response = await api.get(`/day-in-review/${id}/summary/forecast`);
    return response.data;
  }

  async getStaffingSummary(id: string): Promise<StaffingSummary> {
    const response = await api.get(`/day-in-review/${id}/summary/staffing`);
    return response.data;
  }

  async getAnomalies(id: string): Promise<Anomaly[]> {
    const response = await api.get(`/day-in-review/${id}/anomalies`);
    return response.data;
  }

  async getShrinkageComparison(id: string, expectedShrinkage?: number): Promise<ShrinkageComparison> {
    const params = expectedShrinkage !== undefined ? { expectedShrinkage } : {};
    const response = await api.get(`/day-in-review/${id}/shrinkage-comparison`, { params });
    return response.data;
  }
}

export const dayInReviewService = new DayInReviewService();
